// The home's role cards (D-443 ruling 2): what the reader's day holds, as
// counts, each a link to the list it counts with that list's filter applied.
// A card exists only for a reader who can act on what it counts, so a sales
// manager sees the sales card and no planner's overloads, and a planner sees
// no import errors for a lane they do not hold.
//
// `useHomeDay` makes every read and reports one settle flag, because the
// screen reveals once (§5): the cards decide the layout, so nothing may appear
// after the reveal. The card components only render what it hands them.

import type { ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { client } from "../../../lib/api";
import { Card } from "../../../components/card";
import { IconArrowRight } from "../../../components/icons";
import {
  shellCountLabel,
  useOpenWorkOrderCountRead,
  type ShellCount,
  type ShellCountRead,
} from "../../../routes/shell-data";
import { useInventoryPermissions } from "../inventory/hooks/permissions";
import { useProductionPermissions } from "../production/hooks/permissions";
import { deriveQualityPermissions } from "../quality/hooks/permissions";
import { canWriteOpportunities, useSalesPermissions } from "../sales/hooks/permissions";
import { usePlanningPermissions } from "../planning/hooks/permissions";
import { usePlanningFacilities } from "../planning/hooks/planning-queries";
import { useScheduleHeads, type ScheduleHeads } from "../planning/hooks/schedule-queries";
import { usePlannedOrderSummary } from "../planning/hooks/mrp-queries";
import { useRoleCodesFeed } from "../../../lib/role-codes";
import { HOME_COUNT_LIMIT, exactCount, hasAny, useHomeCount, useMyUserId } from "./home-reads";

/* --------------------------------- reads --------------------------------- */

/** The planning area remembers the facility a planner last chose under this
 * key (planning-layout.tsx). The home reads it so the planner's card speaks
 * about the same facility their planning screens do. */
const PLANNING_FACILITY_KEY = "planning.facility";

function rememberedPlanningFacility(): string | undefined {
  try {
    return window.localStorage.getItem(PLANNING_FACILITY_KEY) ?? undefined;
  } catch {
    return undefined;
  }
}

/** The facility a planner card speaks about: the remembered one when it is
 * still in the list, else the first (pure). */
export function choosePlanningFacility<T extends { readonly id: string }>(
  facilities: readonly T[],
  remembered: string | undefined,
): T | undefined {
  return facilities.find((f) => f.id === remembered) ?? facilities[0];
}

interface FacilityLike {
  readonly id: string;
  readonly name: string;
}

export interface HomeDay {
  readonly settled: boolean;
  readonly updatedAt: number;
  readonly production?: {
    readonly open: ShellCountRead;
    readonly overdue: ShellCountRead;
    readonly drafts?: ShellCountRead;
  };
  readonly planning?: {
    readonly facility: FacilityLike | undefined;
    readonly heads?: { readonly data: ScheduleHeads | undefined; readonly failed: boolean };
    readonly messages?: ShellCount;
    readonly purchases?: ShellCount;
    readonly release?: ShellCount;
  };
  readonly sales?: {
    readonly toSend?: ShellCountRead;
    readonly sent?: ShellCountRead;
    readonly reviews?: ShellCountRead;
    readonly stalling?: ShellCountRead;
    readonly me: string | undefined;
  };
  readonly warehouse?: {
    readonly receive?: ShellCountRead;
    readonly ship?: ShellCountRead;
  };
  readonly quality?: {
    readonly inspections?: ShellCountRead;
    readonly quarantine?: ShellCountRead;
  };
}

/** Every role card's reads, gated per code, with one settle flag. */
export function useHomeDay(): HomeDay {
  const feed = useRoleCodesFeed();
  const production = useProductionPermissions();
  const inventory = useInventoryPermissions();
  const sales = useSalesPermissions();
  const planning = usePlanningPermissions();
  const quality = deriveQualityPermissions(feed);

  // Production: a reader who writes or releases work orders, over a read that
  // reaches a real book. qc, maintenance, finance and the storekeeper read
  // work orders and act on none, so the read code alone draws no card
  // (D-443 ruling 1). A sales reader's read is their own book, empty by
  // construction (D-290 ruling 9).
  const canProduction =
    production.has("production.read") &&
    !production.readsOwnBookOnly &&
    (production.has("production.wo.write") || production.has("production.wo.release"));
  const canRelease = canProduction && production.has("production.wo.release");
  const open = useOpenWorkOrderCountRead(canProduction);
  const overdue = useHomeCount("wo-overdue", canProduction, async (signal) => {
    const { data } = await client.GET("/v1/work-orders", {
      signal,
      params: {
        query: { status: ["released", "in_progress"], overdue: "true", limit: HOME_COUNT_LIMIT },
      },
    });
    return data;
  });
  const drafts = useHomeCount("wo-draft", canRelease, async (signal) => {
    const { data } = await client.GET("/v1/work-orders", {
      signal,
      params: { query: { status: ["draft"], limit: HOME_COUNT_LIMIT } },
    });
    return data;
  });

  // Planning: the planner's own levers. `planning.schedule.read` alone is held
  // by sales and maintenance too, who cannot act on an overload.
  const isPlanner =
    planning.has("planning.planned_order.manage") || planning.has("planning.schedule.publish");
  const canSchedule = isPlanner && planning.has("planning.schedule.read");
  const canOrders = isPlanner && planning.has("planning.planned_order.read");
  // Only a planner's card names a facility; the list needs identity.view_org,
  // so nobody else sends it (D-040).
  const facilities = usePlanningFacilities(isPlanner);
  const facilityList = (isPlanner ? facilities.data?.items : undefined) ?? [];
  const facility = choosePlanningFacility(facilityList, rememberedPlanningFacility());
  const facilityId = facility?.id ?? "";
  // Neither read polls here: the home reveals once (§5), and a draft or a
  // first publish arriving a minute later would move the card under the
  // reader. The planning screens keep their polls.
  const heads = useScheduleHeads(facilityId, canSchedule && facilityId.length > 0, false, false);
  const summary = usePlannedOrderSummary(facilityId, canOrders && facilityId.length > 0, false);
  const facilitiesSettled = !isPlanner || facilities.isFetched;
  const planningSettled =
    facilitiesSettled &&
    (facilityId.length === 0 ||
      ((!canSchedule || heads.isFetched || heads.isError) &&
        (!canOrders || summary.isFetched || summary.isError)));

  // Sales: the reader's own quotes and deals, and the approvals waiting on a
  // reviewer.
  const canQuotes = sales.canOpenQuotes;
  // Deals: the reader who works them. Planner and finance read every deal
  // and own none, so a stale count of "theirs" would always be empty noise.
  const canDeals = sales.canOpenPipeline && canWriteOpportunities(sales);
  const canReviews = sales.canOpenReviews;
  const wantsMe = canQuotes || canDeals;
  const me = useMyUserId(wantsMe);
  const mine = me.id !== undefined;
  const toSend = useHomeCount(
    `quotes-approved-${me.id ?? ""}`,
    canQuotes && mine,
    async (signal) => {
      const { data } = await client.GET("/v1/quotes", {
        signal,
        params: {
          query: { status: "approved", owner_user_id: me.id ?? "", limit: HOME_COUNT_LIMIT },
        },
      });
      return data;
    },
  );
  const sent = useHomeCount(`quotes-sent-${me.id ?? ""}`, canQuotes && mine, async (signal) => {
    const { data } = await client.GET("/v1/quotes", {
      signal,
      params: { query: { status: "sent", owner_user_id: me.id ?? "", limit: HOME_COUNT_LIMIT } },
    });
    return data;
  });
  const reviews = useHomeCount("sales-reviews", canReviews, async (signal) => {
    const { data } = await client.GET("/v1/sales/reviews", {
      signal,
      params: { query: { limit: HOME_COUNT_LIMIT } },
    });
    return data;
  });
  const stalling = useHomeCount(
    `deals-rotting-${me.id ?? ""}`,
    canDeals && mine,
    async (signal) => {
      const { data } = await client.GET("/v1/opportunities", {
        signal,
        params: {
          query: { rotting: "true", owner_user_id: me.id ?? "", limit: HOME_COUNT_LIMIT },
        },
      });
      return data;
    },
  );

  // Warehouse: what arrives and what leaves today.
  const canReceive = inventory.has("inventory.transfer.receive");
  const canShip = sales.has("sales.shipment.post");
  const receive = useHomeCount("transfers-dispatched", canReceive, async (signal) => {
    const { data } = await client.GET("/v1/inventory/transfers", {
      signal,
      params: { query: { status: "dispatched", limit: HOME_COUNT_LIMIT } },
    });
    return data;
  });
  const ship = useHomeCount("orders-shippable", canShip, async (signal) => {
    const { data } = await client.GET("/v1/orders", {
      signal,
      params: { query: { shippable: "true", limit: HOME_COUNT_LIMIT } },
    });
    return data;
  });

  // Quality: the inspector's queue and the board a disposer works.
  const canInspect = quality.has("quality.inspection.record");
  const canDispose = quality.has("quality.hold.disposition");
  const inspections = useHomeCount("inspections-open", canInspect, async (signal) => {
    const { data } = await client.GET("/v1/quality/inspections", {
      signal,
      params: { query: { status: "open", limit: HOME_COUNT_LIMIT } },
    });
    return data;
  });
  const quarantine = useHomeCount("holds-active", canDispose, async (signal) => {
    const { data } = await client.GET("/v1/quality/holds", {
      signal,
      params: { query: { status: "active", limit: HOME_COUNT_LIMIT } },
    });
    return data;
  });

  const permsResolved =
    feed.isResolved &&
    production.isResolved &&
    inventory.isResolved &&
    sales.isResolved &&
    planning.isResolved;

  const counts = [
    overdue,
    drafts,
    toSend,
    sent,
    reviews,
    stalling,
    receive,
    ship,
    inspections,
    quarantine,
  ];
  const settled =
    permsResolved &&
    (!canProduction || open.isSettled) &&
    me.isSettled &&
    counts.every((c) => c.isSettled) &&
    planningSettled;

  const summaryData = summary.data;
  const messages =
    summaryData === undefined
      ? undefined
      : Object.values(summaryData.action_messages).reduce((sum, n) => sum + n, 0);

  return {
    settled,
    updatedAt: Math.max(
      open.updatedAt,
      heads.dataUpdatedAt,
      summary.dataUpdatedAt,
      ...counts.map((c) => c.updatedAt),
    ),
    ...(canProduction ? { production: { open, overdue, ...(canRelease ? { drafts } : {}) } } : {}),
    // A planner with no facility to speak about (the list failed, or the
    // tenant has none yet) gets no card rather than rows about nothing.
    ...(isPlanner && facility !== undefined
      ? {
          planning: {
            facility,
            // A refetch that fails while the rows it had are still held is
            // not a failure to read: the card keeps its rows.
            ...(canSchedule
              ? { heads: { data: heads.data, failed: heads.isError && heads.data === undefined } }
              : {}),
            ...(canOrders
              ? {
                  messages: exactCount(messages),
                  purchases: exactCount(
                    summaryData === undefined ? undefined : summaryData.by_status.proposed.purchase,
                  ),
                  release: exactCount(
                    summaryData === undefined
                      ? undefined
                      : summaryData.by_status.firmed.manufacture,
                  ),
                }
              : {}),
          },
        }
      : {}),
    ...(canQuotes || canDeals || canReviews
      ? {
          sales: {
            me: me.id,
            ...(canQuotes ? { toSend, sent } : {}),
            ...(canReviews ? { reviews } : {}),
            ...(canDeals ? { stalling } : {}),
          },
        }
      : {}),
    ...(canReceive || canShip
      ? {
          warehouse: {
            ...(canReceive ? { receive } : {}),
            ...(canShip ? { ship } : {}),
          },
        }
      : {}),
    ...(canInspect || canDispose
      ? {
          quality: {
            ...(canInspect ? { inspections } : {}),
            ...(canDispose ? { quarantine } : {}),
          },
        }
      : {}),
  };
}

/* -------------------------------- render --------------------------------- */

type Tone = "bad" | "warn" | undefined;

/** The label, the number and the arrow of one row. The number is the count
 * read's own label ("12", "50+"), or a dash when the read failed. */
function RowBody({
  label,
  count,
  tone,
  t,
}: {
  label: string;
  count: ShellCount;
  tone?: Tone;
  t: TFunction;
}) {
  const value = shellCountLabel(count, t) ?? t("count.unknown");
  const lit = tone !== undefined && hasAny(count);
  return (
    <>
      <span className="home-day-label">{label}</span>
      <span
        className={`home-day-count num${lit ? ` home-day-count-${tone}` : ""}${
          count.kind === "exact" && count.n === 0 ? " home-day-count-zero" : ""
        }`}
      >
        {value}
      </span>
      <span className="home-day-go" aria-hidden="true">
        <IconArrowRight />
      </span>
    </>
  );
}

function DayCard({
  title,
  meta,
  testId,
  children,
}: {
  title: string;
  meta?: string | undefined;
  testId: string;
  children: ReactNode;
}) {
  return (
    <Card
      title={title}
      action={meta === undefined ? undefined : <span className="home-day-meta">{meta}</span>}
      data-testid={testId}
    >
      <ul className="home-day-list">{children}</ul>
    </Card>
  );
}

export function HomeDayCards({ day }: { day: HomeDay }) {
  const { t } = useTranslation();
  const cards: ReactNode[] = [];

  if (day.production !== undefined) {
    const p = day.production;
    cards.push(
      <DayCard key="production" title={t("home.day.production")} testId="home-day-production">
        <li>
          <Link
            to="/production/work-orders"
            search={{ status: ["released", "in_progress"] }}
            className="home-day-row"
            data-testid="kpi-open-wos"
          >
            <RowBody label={t("home.day.openWos")} count={p.open.count} t={t} />
          </Link>
        </li>
        <li>
          <Link
            to="/production/work-orders"
            // The filter the count used: the server's overdue filter alone
            // also takes drafts (D-443 ruling 2).
            search={{ status: ["released", "in_progress"], overdue: true }}
            className="home-day-row"
            data-testid="home-day-wo-overdue"
          >
            <RowBody label={t("home.day.overdueWos")} count={p.overdue.count} tone="bad" t={t} />
          </Link>
        </li>
        {p.drafts !== undefined ? (
          <li>
            <Link
              to="/production/work-orders"
              search={{ status: ["draft"] }}
              className="home-day-row"
              data-testid="home-day-wo-draft"
            >
              <RowBody label={t("home.day.draftWos")} count={p.drafts.count} t={t} />
            </Link>
          </li>
        ) : null}
      </DayCard>,
    );
  }

  if (day.planning !== undefined) {
    const p = day.planning;
    const published = p.heads?.data?.published ?? null;
    const draft = p.heads?.data?.draft ?? null;
    cards.push(
      <DayCard
        key="planning"
        title={t("home.day.planning")}
        meta={p.facility?.name}
        testId="home-day-planning"
      >
        {p.heads !== undefined ? (
          <>
            <li>
              <Link
                to="/planning/schedule"
                className="home-day-row"
                data-testid="home-day-schedule"
              >
                <span className="home-day-label">
                  {p.heads.failed
                    ? t("home.day.scheduleUnknown")
                    : published === null
                      ? t("home.day.scheduleNone")
                      : t("home.day.schedulePublished", { version: published.version })}
                </span>
                {draft !== null ? (
                  <span className="home-day-chip" data-testid="home-day-schedule-waiting">
                    {t("home.day.scheduleWaiting")}
                  </span>
                ) : null}
                <span className="home-day-go" aria-hidden="true">
                  <IconArrowRight />
                </span>
              </Link>
            </li>
            {published !== null ? (
              <>
                <li>
                  <Link
                    to="/planning/schedule"
                    className="home-day-row"
                    data-testid="home-day-late"
                  >
                    <RowBody
                      label={t("home.day.lateOrders")}
                      count={exactCount(published.kpis.late_orders)}
                      tone="bad"
                      t={t}
                    />
                  </Link>
                </li>
                <li>
                  <Link
                    to="/planning/capacity"
                    className="home-day-row"
                    data-testid="home-day-overloads"
                  >
                    <RowBody
                      label={t("home.day.overloads")}
                      count={exactCount(published.kpis.overloads)}
                      tone="warn"
                      t={t}
                    />
                  </Link>
                </li>
              </>
            ) : null}
          </>
        ) : null}
        {p.messages !== undefined ? (
          <li>
            <Link
              to="/planning/orders"
              search={{ view: "messages", message: "true" }}
              className="home-day-row"
              data-testid="home-day-messages"
            >
              <RowBody label={t("home.day.actionMessages")} count={p.messages} tone="warn" t={t} />
            </Link>
          </li>
        ) : null}
        {p.purchases !== undefined ? (
          <li>
            <Link
              to="/planning/orders"
              search={{ kind: "purchase", status: "proposed" }}
              className="home-day-row"
              data-testid="home-day-purchases"
            >
              <RowBody label={t("home.day.purchases")} count={p.purchases} t={t} />
            </Link>
          </li>
        ) : null}
        {p.release !== undefined ? (
          <li>
            <Link
              to="/planning/orders"
              search={{ kind: "manufacture", status: "firmed" }}
              className="home-day-row"
              data-testid="home-day-release"
            >
              <RowBody label={t("home.day.toRelease")} count={p.release} t={t} />
            </Link>
          </li>
        ) : null}
      </DayCard>,
    );
  }

  if (day.sales !== undefined) {
    const s = day.sales;
    const owner = s.me;
    cards.push(
      <DayCard key="sales" title={t("home.day.sales")} testId="home-day-sales">
        {s.reviews !== undefined ? (
          <li>
            <Link to="/sales/reviews" className="home-day-row" data-testid="home-day-reviews">
              <RowBody label={t("home.day.reviews")} count={s.reviews.count} tone="warn" t={t} />
            </Link>
          </li>
        ) : null}
        {s.toSend !== undefined ? (
          <li>
            <Link
              to="/sales/quotes"
              search={{ status: "approved", ...(owner === undefined ? {} : { owner }) }}
              className="home-day-row"
              data-testid="home-day-quotes-approved"
            >
              <RowBody
                label={t("home.day.quotesToSend")}
                count={s.toSend.count}
                tone="warn"
                t={t}
              />
            </Link>
          </li>
        ) : null}
        {s.sent !== undefined ? (
          <li>
            <Link
              to="/sales/quotes"
              search={{ status: "sent", ...(owner === undefined ? {} : { owner }) }}
              className="home-day-row"
              data-testid="home-day-quotes-sent"
            >
              <RowBody label={t("home.day.quotesSent")} count={s.sent.count} t={t} />
            </Link>
          </li>
        ) : null}
        {s.stalling !== undefined ? (
          <li>
            <Link
              to="/sales/opportunities"
              search={{ rotting: true, ...(owner === undefined ? {} : { owner }) }}
              className="home-day-row"
              data-testid="home-day-deals-stalling"
            >
              <RowBody
                label={t("home.day.dealsStalling")}
                count={s.stalling.count}
                tone="warn"
                t={t}
              />
            </Link>
          </li>
        ) : null}
      </DayCard>,
    );
  }

  if (day.warehouse !== undefined) {
    const w = day.warehouse;
    cards.push(
      <DayCard key="warehouse" title={t("home.day.warehouse")} testId="home-day-warehouse">
        {w.receive !== undefined ? (
          <li>
            <Link
              to="/inventory/transfers"
              search={{ status: "dispatched" }}
              className="home-day-row"
              data-testid="home-day-receive"
            >
              <RowBody label={t("home.day.toReceive")} count={w.receive.count} t={t} />
            </Link>
          </li>
        ) : null}
        {w.ship !== undefined ? (
          <li>
            <Link to="/floor/ship" className="home-day-row" data-testid="home-day-ship">
              <RowBody label={t("home.day.toShip")} count={w.ship.count} t={t} />
            </Link>
          </li>
        ) : null}
      </DayCard>,
    );
  }

  if (day.quality !== undefined) {
    const q = day.quality;
    cards.push(
      <DayCard key="quality" title={t("home.day.quality")} testId="home-day-quality">
        {q.inspections !== undefined ? (
          <li>
            <Link
              to="/quality/inspections"
              search={{ tab: "open" }}
              className="home-day-row"
              data-testid="home-day-inspections"
            >
              <RowBody label={t("home.day.inspections")} count={q.inspections.count} t={t} />
            </Link>
          </li>
        ) : null}
        {q.quarantine !== undefined ? (
          <li>
            <Link
              to="/quality/quarantine"
              className="home-day-row"
              data-testid="home-day-quarantine"
            >
              <RowBody
                label={t("home.day.quarantine")}
                count={q.quarantine.count}
                tone="warn"
                t={t}
              />
            </Link>
          </li>
        ) : null}
      </DayCard>,
    );
  }

  if (cards.length === 0) return null;
  return (
    <div className="home-day" data-testid="home-day">
      {cards}
    </div>
  );
}
